import { useState, useMemo } from 'react'
import { useBuildingStore } from '../../stores/buildingStore'
import { PathfindingEngine } from '../../engine/PathfindingEngine'
import '../../styles/panels.css'

interface RoutingResult {
  path: { x: number; z: number; floor: number }[]
  distance: number
  steps: number
}

interface RoutingPanelProps {
  onRouteCalculated?: (path: { x: number; z: number; floor: number }[]) => void
  onRouteClear?: () => void
}

export default function RoutingPanel({ onRouteCalculated, onRouteClear }: RoutingPanelProps) {
  const badges = useBuildingStore((s) => s.badges)
  const locations = useBuildingStore((s) => s.locations)
  const pathSegments = useBuildingStore((s) => s.pathSegments)

  const [startBadge, setStartBadge] = useState('')
  const [endZone, setEndZone] = useState('')
  const [result, setResult] = useState<RoutingResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const pathfinder = useMemo(() => {
    const engine = new PathfindingEngine()
    if (pathSegments.length > 0) {
      engine.buildGraph(pathSegments)
    }
    return engine
  }, [pathSegments])

  const calculate = () => {
    setError(null)
    setResult(null)

    const badge = badges.find((b) => b.badge_code === startBadge)
    const zone = locations.find((l) => l.id === endZone)

    if (!badge || !zone) {
      setError('Selectionnez un depart et une destination')
      return
    }

    if (pathSegments.length === 0) {
      setError('Aucun segment de chemin disponible')
      return
    }

    const path = pathfinder.findPath(
      { x: zone.x, z: zone.z, floor: badge.default_floor },
      { x: zone.x, z: zone.z, floor: zone.floor }
    )

    if (path.length === 0) {
      setError('Aucun itineraire trouve')
      return
    }

    let totalDist = 0
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1]!
      const b = path[i]!
      totalDist += Math.sqrt((a.x - b.x) ** 2 + (a.z - b.z) ** 2)
    }

    setResult({ path, distance: totalDist, steps: path.length })
    onRouteCalculated?.(path)
  }

  const clear = () => {
    setResult(null)
    setError(null)
    setStartBadge('')
    setEndZone('')
    onRouteClear?.()
  }

  return (
    <div className="routing-panel">
      <div className="panel-section__title">Itineraire</div>

      <div className="routing-panel__field">
        <label className="routing-panel__label">Depart (badge)</label>
        <select
          className="routing-panel__select"
          value={startBadge}
          onChange={(e) => setStartBadge(e.target.value)}
        >
          <option value="">-- Choisir --</option>
          {badges.map((b) => (
            <option key={b.id} value={b.badge_code}>
              {b.name} ({b.badge_code})
            </option>
          ))}
        </select>
      </div>

      <div className="routing-panel__field">
        <label className="routing-panel__label">Destination (zone)</label>
        <select
          className="routing-panel__select"
          value={endZone}
          onChange={(e) => setEndZone(e.target.value)}
        >
          <option value="">-- Choisir --</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} (Etage {l.floor})
            </option>
          ))}
        </select>
      </div>

      <div className="routing-panel__actions">
        <button className="routing-panel__btn" onClick={calculate}>
          Calculer
        </button>
        {result && (
          <button className="routing-panel__btn routing-panel__btn--clear" onClick={clear}>
            Effacer
          </button>
        )}
      </div>

      {error && <div className="routing-panel__error">{error}</div>}

      {result && (
        <div className="routing-panel__result">
          <div className="routing-panel__stat">
            <span>Distance</span>
            <span>{result.distance.toFixed(1)} m</span>
          </div>
          <div className="routing-panel__stat">
            <span>Etapes</span>
            <span>{result.steps}</span>
          </div>
        </div>
      )}
    </div>
  )
}
